import { useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { assignIds, parseNote, type ParsedNote } from "@minne/core";
import { AlertCircle, Plus } from "lucide-react";
import { COMPONENT_TEMPLATES, insertSnippet } from "@/lib/componentTemplates";
import { cn } from "@/lib/utils";
import { BlockRenderer } from "@/components/blocks/BlockRenderer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

/**
 * The Markdown editor used by the new-note and edit pages: Write / Preview tabs
 * (GitHub style, so it fits a phone), an Insert menu that drops a component
 * template at the cursor, and validation from the same parser the server runs.
 */
export function MarkdownEditor({
  id,
  value,
  onChange,
  placeholder,
  autoFocus,
  className,
  onParsed,
}: {
  id?: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  /** The parse result for the current text (ids assigned); pages use its outline for the rail. */
  onParsed?: (parsed: ParsedNote) => void;
}) {
  const [tab, setTab] = useState<"write" | "preview">("write");
  const ref = useRef<HTMLTextAreaElement>(null);
  const pendingSelection = useRef<[number, number] | null>(null);

  // Parse in the browser; deferred so typing never waits on the parser. Ids are
  // assigned first, exactly as the server does on write, so the preview and the
  // error list name the same blocks the saved note will have.
  const deferred = useDeferredValue(value);
  const parsed = useMemo(() => parseNote(assignIds(deferred).markdown), [deferred]);
  const errors = parsed.errors;
  useEffect(() => onParsed?.(parsed), [parsed, onParsed]);

  // Grow with content.
  useEffect(() => {
    const el = ref.current;
    if (!el || tab !== "write") return;
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, 320)}px`;
  }, [value, tab]);

  // Apply the selection an insert asked for once React has committed the new text.
  useLayoutEffect(() => {
    const sel = pendingSelection.current;
    const el = ref.current;
    if (!sel || !el) return;
    pendingSelection.current = null;
    const apply = () => {
      el.focus();
      el.setSelectionRange(sel[0], sel[1]);
    };
    apply();
    // The dropdown's close handling runs after this commit; re-apply once it is done.
    requestAnimationFrame(apply);
  }, [value]);

  function insert(template: (typeof COMPONENT_TEMPLATES)[number]) {
    const el = ref.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = insertSnippet(value, start, end, template);
    pendingSelection.current = [next.selectionStart, next.selectionEnd];
    setTab("write");
    onChange(next.text);
  }

  return (
    <Tabs value={tab} onValueChange={(v) => setTab(v as "write" | "preview")} className={className}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <TabsList>
          <TabsTrigger value="write">Write</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm">
              <Plus /> Insert
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64" onCloseAutoFocus={(e) => e.preventDefault()}>
            {COMPONENT_TEMPLATES.map((t) => (
              <DropdownMenuItem key={t.name} onSelect={() => insert(t)}>
                <div className="min-w-0">
                  <div className="font-medium">{t.label}</div>
                  <div className="truncate text-xs text-muted-foreground">{t.hint}</div>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <TabsContent value="write" forceMount className={cn(tab !== "write" && "hidden")}>
        <Textarea
          ref={ref}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          className="min-h-80 resize-none font-mono text-base leading-relaxed md:text-[13.5px]"
          aria-label="Markdown"
          aria-invalid={errors.length > 0 || undefined}
        />
        {errors.length > 0 ? (
          <ul className="mt-2 space-y-1 text-xs" aria-live="polite">
            {errors.map((e, i) => (
              <li key={`${e.blockId}-${i}`} className="flex items-start gap-1.5 text-destructive">
                <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
                <span>
                  {e.blockId && <code className="font-mono">{e.blockId}: </code>}
                  {e.message}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-xs text-muted-foreground">
            Components are fenced blocks, for example <code className="font-mono">```callout tone=tip</code> … <code className="font-mono">```</code>. Use Insert for a filled-in example. The server assigns block ids.
          </p>
        )}
      </TabsContent>

      <TabsContent value="preview">
        <div className="min-h-80 rounded-lg border bg-card px-4 py-5 sm:px-6">
          {parsed.blocks.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing to preview yet.</p>
          ) : (
            <BlockRenderer blocks={parsed.blocks} />
          )}
        </div>
        {errors.length > 0 && (
          <p className="mt-2 text-xs text-destructive">
            {errors.length} {errors.length === 1 ? "block has" : "blocks have"} errors and will render as raw code. Details on the Write tab.
          </p>
        )}
      </TabsContent>
    </Tabs>
  );
}
